import React, { useEffect, useMemo, useRef, useState } from "react";
import {
    Row,
    Col,
    Form,
    Collapse,
    InputGroup,
    Table,
    Image,
} from "react-bootstrap";
import { Field, useFormikContext } from "formik";
import { FaChevronDown, FaChevronUp } from "react-icons/fa";

import FormSection from "../FormSection";
import MoneyField from "../MoneyField";

import DateRangePicker from "../../../../components/Form/DateRangePicker";
import SmartSearchSelect from "../../../../components/Smart/SmartSearchSelect";
import PhoneInput from "../../../../components/Form/PhoneInput";
import placeholderImg from "../../../../assets/images/moto-placeholder.png";

import { handleSafeSelectChange } from "../../../../utils/handleSafeSelectChange";
import { useFormat } from "../../../../components/FormatContext";

import { locationApi, shopApi } from "../../services/booking.api";
import { toCents, fromCents } from "../../utils/money";

import AccessoriesDrawer from "./accessories/AccessoriesDrawer";
import AccessoryComparisonTable from "./accessories/AccessoryComparisonTable";

/* ------------------------------------ AssetsTab 主组件 ------------------------------------ */

const AssetsTab = ({
    shopMotorcycleExtras,
    countryOptions,
    originalRentalOrderExtra,

    cityOptions,
    setCityOptions,
    locationOptions,
    setLocationOptions,
    shopsOptions,
    setShopsOptions,
    motorcycleOptions,
    setMotorcycleOptions,

    markDirtyPricing,
    originalServicesTotalCentsRef,
}) => {

    const sizeOptions = useMemo(() => ({
        e1: ["6英寸", "7英寸", "8英寸"],
        e2: ["标准", "加大"],
        e3: ["小号", "中号", "大号"],
        e4: ["30L", "40L", "50L"],
        e5: ["通用", "专用"],
        e6: ["标准", "加长"],
    }), []);

    const { setFieldValue, values, setValues } = useFormikContext();
    const { formatCurrency } = useFormat();

    const [showMotorcycleAdvanced, setShowMotorcycleAdvanced] = useState(false);
    const [showShopAdvanced, setShowShopAdvanced] = useState(false);
    const [showAccessoriesModal, setShowAccessoriesModal] = useState(false);

    const generateTimeOptions = () => {
        const options = [];
        for (let hour = 7; hour <= 24; hour++) {
            for (let min = 0; min < 60; min += 30) {
                if (hour === 24 && min > 0) break;
                const hh = hour.toString().padStart(2, "0");
                const mm = min.toString().padStart(2, "0");
                options.push(`${hh}:${mm}`);
            }
        }
        return options;
    };

    const timeOptions = useMemo(() => generateTimeOptions(), []);

    const updateAccessories = (newAccessories) => {
        setValues((prev) => ({
            ...prev,
            rentalOrderExtra: newAccessories,
        }));
        markDirtyPricing();
    };

    // 国家 -> 城市
    useEffect(() => {
        const loadCities = async () => {
            const countryId = values?.shopSnapshot?.pickup?.location?.country?.id;
            if (!countryId) {
                setCityOptions([]);
                return;
            }
            try {
                const data = await locationApi.getCities(countryId);
                setCityOptions(data || []);
            } catch (error) {
                console.error("Failed to fetch cities:", error);
                setCityOptions([]);
            }
        };
        loadCities();
    }, [values?.shopSnapshot?.pickup?.location?.country?.id, setCityOptions]);

    // 城市 -> location
    useEffect(() => {
        const loadLocations = async () => {
            const cityId = values?.shopSnapshot?.pickup?.location?.city?.id;
            if (!cityId) {
                setLocationOptions([]);
                return;
            }
            try {
                const data = await locationApi.getLocations(cityId);
                setLocationOptions(data || []);
            } catch (error) {
                console.error("Failed to fetch locations:", error);
                setLocationOptions([]);
            }
        };
        loadLocations();
    }, [values?.shopSnapshot?.pickup?.location?.city?.id, setLocationOptions]);

    // location -> shops
    useEffect(() => {
        const loadShops = async () => {
            const locationId = values?.shopSnapshot?.pickup?.location?.id;
            if (!locationId) {
                setShopsOptions([]);
                return;
            }
            try {
                const data = await shopApi.getByLocation(locationId);
                setShopsOptions(data || []);
            } catch (error) {
                console.error("Failed to fetch shops:", error);
                setShopsOptions([]);
            }
        };
        loadShops();
    }, [values?.shopSnapshot?.pickup?.location?.id, setShopsOptions]);

    // shop -> 拉 shop info + motorcycles options
    const firstRenderRef = useRef(true);
    useEffect(() => {
        if (firstRenderRef.current) {
            firstRenderRef.current = false;
            return;
        }

        const loadShopInfo = async () => {
            const shopId = values?.shopSnapshot?.pickup?.shop?.id;

            if (!shopId) {
                // reset shop related
                setValues(
                    (prev) => ({
                        ...prev,
                        rentalOrder: {
                            ...prev.rentalOrder,
                            supplierId: "",
                        },
                        shopSnapshot: {
                            ...prev.shopSnapshot,
                            pickup: {
                                ...prev.shopSnapshot.pickup,
                                shop: {
                                    ...prev.shopSnapshot.pickup.shop,
                                    companyName: "",
                                    address: "",
                                    prefix: "",
                                    phone: "",
                                    email: "",
                                    shopType: "",
                                    policy: "",
                                    language: "",
                                    distanceUnit: "",
                                },
                            },
                        },
                        pricingSnapshot: {
                            ...prev.pricingSnapshot,
                            calculation: {
                                ...prev.pricingSnapshot.calculation,
                                supplierCurrencyCode: "",
                            },
                        },
                    }),
                    false
                );
                setMotorcycleOptions([]);
                return;
            }

            try {
                const data = await shopApi.getById(shopId);

                setValues((prev) => ({
                    ...prev,
                    rentalOrder: {
                        ...prev.rentalOrder,
                        supplierId: data?.supplierId || "",
                    },
                    shopSnapshot: {
                        ...prev.shopSnapshot,
                        pickup: {
                            ...prev.shopSnapshot.pickup,
                            shop: {
                                ...prev.shopSnapshot.pickup.shop,
                                name: data?.name || "",
                                depositMethod: data?.depositMethod || "",
                                paymentMethod: data?.paymentMethod || "",
                                companyName: data?.companyName || "",
                                address: data?.address || "",
                                prefix: data?.prefix || "",
                                phone: data?.phone || "",
                                email: data?.email || "",
                                shopType: data?.shopType || "",
                                policy: data?.policy || "",
                                language: data?.language || "",
                                distanceUnit: data?.distanceUnit || "",
                                durationType:data?.rentalDetail.durationType
                            },
                            location: {
                                id: data?.location?.id || "",
                                name: data?.location?.id || "",
                                city: {
                                    id: data?.location?.City?.id || "",
                                    name: data?.location?.City?.name || "",
                                },
                                country: {
                                    id: data?.location?.City?.Country?.id || "",
                                    ISOCode: data?.location?.City?.Country?.ISOCode || "",
                                },
                            },
                        },
                    },
                    pricingSnapshot: {
                        ...prev.pricingSnapshot,
                        calculation: {
                            ...prev.pricingSnapshot.calculation,
                            supplierCurrencyCode: data?.currency || "",
                        },
                    },
                    rentalPolicySnapshot: {
                        ...prev.rentalPolicySnapshot,
                        cancellation7DaysRefund: data?.rentalDetail?.cancellation7DaysRefund ?? "",
                        cancellation15DaysRefund: data?.rentalDetail?.cancellation15DaysRefund ?? "",
                        cancellation30DaysRefund: data?.rentalDetail?.cancellation30DaysRefund ?? "",
                        cancellation60DaysRefund: data?.rentalDetail?.cancellation60DaysRefund ?? "",
                    },
                }));

                setMotorcycleOptions(
                    (data?.shopMotorcycleInfo || []).map((motorcycle) => ({
                        value: motorcycle.id,
                        label: `${motorcycle.brandName} ${motorcycle.modelName}`,
                    }))
                );
            } catch (error) {
                console.error("Failed to fetch shop by id:", error);
            }
        };

        loadShopInfo();
    }, [values?.shopSnapshot?.pickup?.shop?.id, setValues, setMotorcycleOptions]);

    const resetAfterCountry = () => {
        setValues(
            (prev) => ({
                ...prev,
                shopSnapshot: {
                    ...prev.shopSnapshot,
                    pickup: {
                        ...prev.shopSnapshot.pickup,
                        shop: {
                            ...prev.shopSnapshot.pickup.shop,
                            id: "",
                        },
                        location: {
                            ...prev.shopSnapshot.pickup.location,
                            id: "",
                            city: { id: "" },
                        },
                    },
                },
            }),
            false
        );
        setCityOptions([]);
        setLocationOptions([]);
        setShopsOptions([]);
    };

    const resetAfterCity = () => {
        setValues(
            (prev) => ({
                ...prev,
                shopSnapshot: {
                    ...prev.shopSnapshot,
                    pickup: {
                        ...prev.shopSnapshot.pickup,
                        shop: {
                            ...prev.shopSnapshot.pickup.shop,
                            id: "",
                        },
                        location: {
                            ...prev.shopSnapshot.pickup.location,
                            id: "",
                        },
                    },
                },
            }),
            false
        );
        setLocationOptions([]);
        setShopsOptions([]);
    };

    const resetAfterLocation = () => {
        setFieldValue("shopSnapshot.pickup.shop.id", "", false);
        setShopsOptions([]);
    };

    const resetAfterShop = () => {
        setFieldValue("motorcycleSnapshot.shopMotorcycle.id", "", false);
        setMotorcycleOptions([]);
    };

    console.log(values?.rentalOrderExtra);
    console.log(originalRentalOrderExtra);
    

    return (
        <FormSection title="Rental Details">
            {/* ---------------- Shop Info ---------------- */}
            <FormSection title="Shop Information" level={2}>
                <Row>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label className="fw-5">
                                Shop Country <span className="text-danger">*</span>
                            </Form.Label>
                            <SmartSearchSelect
                                currentID={values?.shopSnapshot?.pickup?.location?.country?.id || ""}
                                options={countryOptions}
                                onChange={async (selectedCountry) => {
                                    handleSafeSelectChange(
                                        values?.shopSnapshot?.pickup?.location?.country?.id,
                                        selectedCountry,
                                        ({ nextValue }) => {
                                            resetAfterCountry();
                                            setFieldValue("shopSnapshot.pickup.location.country.id", nextValue || "", false);
                                        }
                                    );
                                }}
                                placeholder="Select country..."
                            />
                        </Form.Group>
                    </Col>

                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label className="fw-5">
                                Shop City <span className="text-danger">*</span>
                            </Form.Label>
                            <SmartSearchSelect
                                currentID={values?.shopSnapshot?.pickup?.location?.city?.id || ""}
                                options={cityOptions}
                                onChange={async (selectedCity) => {
                                    handleSafeSelectChange(
                                        values?.shopSnapshot?.pickup?.location?.city?.id,
                                        selectedCity,
                                        ({ nextValue }) => {
                                            resetAfterCity();
                                            setFieldValue("shopSnapshot.pickup.location.city.id", nextValue || "", false);
                                        }
                                    );
                                }}
                                placeholder="Select city..."
                            />
                        </Form.Group>
                    </Col>

                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label className="fw-5">
                                Shop Location <span className="text-danger">*</span>
                            </Form.Label>
                            <SmartSearchSelect
                                currentID={values?.shopSnapshot?.pickup?.location?.id || ""}
                                options={locationOptions}
                                onChange={async (selectedLocaiton) => {
                                    handleSafeSelectChange(
                                        values?.shopSnapshot?.pickup?.location?.id,
                                        selectedLocaiton,
                                        ({ nextValue }) => {
                                            resetAfterLocation();
                                            setFieldValue("shopSnapshot.pickup.location.id", nextValue || "", false);
                                        }
                                    );
                                }}
                                placeholder="Select location..."
                            />
                        </Form.Group>
                    </Col>

                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label className="fw-5">
                                Shop <span className="text-danger">*</span>
                            </Form.Label>
                            <SmartSearchSelect
                                currentID={values?.shopSnapshot?.pickup?.shop?.id || ""}
                                options={shopsOptions}
                                onChange={(selectedShop) => {
                                    handleSafeSelectChange(
                                        values?.shopSnapshot?.pickup?.shop?.id,
                                        selectedShop,
                                        ({ nextValue }) => {
                                            resetAfterShop();
                                            setFieldValue("shopSnapshot.pickup.shop.id", nextValue || "", false);
                                        }
                                    );
                                }}
                                placeholder="Select shop..."
                            />
                        </Form.Group>
                    </Col>

                    <Col md={6}>
                        <PhoneInput
                            label="Shop Phone"
                            prefixField="shopSnapshot.pickup.shop.prefix"
                            phoneField="shopSnapshot.pickup.shop.phone"
                        />
                    </Col>

                    <Col md={6}>
                        <Field name="shopSnapshot.pickup.shop.email">
                            {({ field, meta }) => (
                                <Form.Group className="mb-3">
                                    <Form.Label>Shop Email</Form.Label>
                                    <Form.Control {...field} type="text" isInvalid={meta.touched && !!meta.error} />
                                    <Form.Control.Feedback type="invalid">{meta.error}</Form.Control.Feedback>
                                </Form.Group>
                            )}
                        </Field>
                    </Col>

                    <Col md={6}>
                        <Field name="shopSnapshot.pickup.shop.address">
                            {({ field, meta }) => (
                                <Form.Group className="mb-3">
                                    <Form.Label>Shop Address</Form.Label>
                                    <Form.Control {...field} type="text" isInvalid={meta.touched && !!meta.error} />
                                    <Form.Control.Feedback type="invalid">{meta.error}</Form.Control.Feedback>
                                </Form.Group>
                            )}
                        </Field>
                    </Col>

                    <Col md={6}>
                        <Form.Label>Rental Period Calculation</Form.Label>
                        <div className="mt-1">
                            {values?.shopSnapshot?.pickup?.shop?.durationType === "DAILY"
                                ? "Natural Days"
                                : "24-hour Periods"}
                        </div>
                    </Col>
                </Row>

                <div className="mb-3">
                    <span
                        onClick={() => setShowShopAdvanced(!showShopAdvanced)}
                        aria-controls="advanced-settings-collapse"
                        aria-expanded={showShopAdvanced}
                        className="d-flex align-items-center cursor-pointer text-primary"
                        style={{ cursor: "pointer" }}
                    >
                        {showShopAdvanced ? (
                            <>
                                <FaChevronUp className="me-2" /> <span>Hide Advanced <b>Shop</b> Settings</span>
                            </>
                        ) : (
                            <>
                                <FaChevronDown className="me-2" /> <span>Show Advanced <b>Shop</b> Settings</span>
                            </>
                        )}
                    </span>
                </div>

                <Collapse in={showShopAdvanced} className="px-0">
                    <div>
                        <Row>
                            <Col md={6}>
                                <Field name="shopSnapshot.pickup.shop.language">
                                    {({ field, meta }) => (
                                        <Form.Group className="mb-3">
                                            <Form.Label>Shop Language</Form.Label>
                                            <Form.Control {...field} type="text" readOnly isInvalid={meta.touched && !!meta.error} />
                                            <Form.Control.Feedback type="invalid">{meta.error}</Form.Control.Feedback>
                                        </Form.Group>
                                    )}
                                </Field>
                            </Col>

                            <Col md={6}>
                                <Field name="shopSnapshot.pickup.shop.distanceUnit">
                                    {({ field, meta }) => (
                                        <Form.Group className="mb-3">
                                            <Form.Label>Shop Distance Unit</Form.Label>
                                            <Form.Control {...field} type="text" readOnly isInvalid={meta.touched && !!meta.error} />
                                            <Form.Control.Feedback type="invalid">{meta.error}</Form.Control.Feedback>
                                        </Form.Group>
                                    )}
                                </Field>
                            </Col>

                            <Col md={6}>
                                <Field name="shopSnapshot.pickup.shop.shopType">
                                    {({ field, meta }) => (
                                        <Form.Group className="mb-3">
                                            <Form.Label>Shop Type</Form.Label>
                                            <Form.Control {...field} type="text" readOnly isInvalid={meta.touched && !!meta.error} />
                                            <Form.Control.Feedback type="invalid">{meta.error}</Form.Control.Feedback>
                                        </Form.Group>
                                    )}
                                </Field>
                            </Col>

                            <Col md={12}>
                                <Field name="shopSnapshot.pickup.shop.policy">
                                    {({ field, meta }) => (
                                        <Form.Group className="mb-3">
                                            <Form.Label>Shop Rental Policy</Form.Label>
                                            <Form.Control {...field} as="textarea" style={{ minHeight: "50px" }} isInvalid={meta.touched && !!meta.error} />
                                            <Form.Control.Feedback type="invalid">{meta.error}</Form.Control.Feedback>
                                        </Form.Group>
                                    )}
                                </Field>
                            </Col>
                        </Row>
                    </div>
                </Collapse>
            </FormSection>

            {/* ---------------- Date Info ---------------- */}
            <FormSection title="Date Information" level={2}>
                <Row>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label className="mb-2">
                                Rental Date : <span className="text-danger">*</span>
                            </Form.Label>
                            <div className="d-flex align-items-center">
                                <DateRangePicker
                                    startDate={new Date(values?.rentalOrder?.startDate)}
                                    endDate={new Date(values?.rentalOrder?.endDate)}
                                    onChange={({ startDate, endDate }) => {
                                        setFieldValue("rentalOrder.startDate", startDate);
                                        setFieldValue("rentalOrder.endDate", endDate);
                                    }}
                                    minDate={new Date("now")}
                                    maxDate={new Date("2030-06-01")}
                                />
                            </div>
                        </Form.Group>
                    </Col>

                    <Col md={6}>
                        <Form.Label>Days</Form.Label>
                        <div className="mt-1">{values?.pricingSnapshot?.calculation?.days}</div>
                    </Col>
                </Row>

                <Row>
                    <Col md={6}>
                        <Field name="rentalOrder.startTime">
                            {({ field, meta }) => (
                                <Form.Group className="mb-3">
                                    <Form.Label>PickUp Time</Form.Label>
                                    <Form.Select {...field} isInvalid={meta.touched && !!meta.error}>
                                        {timeOptions.map((time) => (
                                            <option key={time} value={time}>
                                                {time}
                                            </option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            )}
                        </Field>
                    </Col>

                    <Col md={6}>
                        <Field name="rentalOrder.endTime">
                            {({ field, meta }) => (
                                <Form.Group className="mb-3">
                                    <Form.Label>DropOff Time</Form.Label>
                                    <Form.Select {...field} isInvalid={meta.touched && !!meta.error}>
                                        {timeOptions.map((time) => (
                                            <option key={time} value={time}>
                                                {time}
                                            </option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            )}
                        </Field>
                    </Col>
                </Row>
            </FormSection>

            {/* ---------------- Motorcycle Info ---------------- */}
            <FormSection title="Motorcycle Information" level={2}>
                <Row>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label className="fw-5">
                                Motorcycle <span className="text-danger">*</span>
                            </Form.Label>
                            <SmartSearchSelect
                                currentID={values?.motorcycleSnapshot?.shopMotorcycle?.id || ""}
                                options={motorcycleOptions}
                                onChange={(selectedMotorcycle) => {
                                    handleSafeSelectChange(
                                        values?.motorcycleSnapshot?.shopMotorcycle?.id,
                                        selectedMotorcycle,
                                        ({ nextValue }) => {
                                            setFieldValue("motorcycleSnapshot.shopMotorcycle.id", nextValue || "", false);
                                            markDirtyPricing();
                                        }
                                    );
                                }}
                                placeholder="Select motorcycle..."
                            />
                        </Form.Group>

                        <Field name="motorcycleSnapshot.shopMotorcycle.motorcycleModel.type">
                            {({ field, meta }) => (
                                <Form.Group className="mb-3">
                                    <Form.Label>Motorcycle Type</Form.Label>
                                    <Form.Control {...field} type="text" readOnly isInvalid={meta.touched && !!meta.error} />
                                    <Form.Control.Feedback type="invalid">{meta.error}</Form.Control.Feedback>
                                </Form.Group>
                            )}
                        </Field>

                        <MoneyField
                            name="pricingSnapshot.calculation.motorcycleTotalWithFeesCents"
                            label="Motorcycle Total"
                            currencyCode={values?.pricingSnapshot?.calculation?.customerCurrencyCode}
                        />
                    </Col>

                    <Col md={6}>
                        <Image
                            src={
                                values?.motorcycleSnapshot?.shopMotorcycle?.motorcycleModel?.photoUrl ||
                                placeholderImg
                            }
                            fluid
                            thumbnail
                            className="mb-3"
                            style={{ maxHeight: 260, objectFit: "contain", width: "100%" }}
                        />
                    </Col>
                </Row>

                <div className="mb-3">
                    <span
                        onClick={() => setShowMotorcycleAdvanced(!showMotorcycleAdvanced)}
                        aria-controls="advanced-settings-collapse"
                        aria-expanded={showMotorcycleAdvanced}
                        className="d-flex align-items-center cursor-pointer text-primary"
                        style={{ cursor: "pointer" }}
                    >
                        {showMotorcycleAdvanced ? (
                            <>
                                <FaChevronUp className="me-2" /> Hide Advanced Motorcycle Settings
                            </>
                        ) : (
                            <>
                                <FaChevronDown className="me-2" /> Show Advanced Motorcycle Settings
                            </>
                        )}
                    </span>
                </div>

                <Collapse in={showMotorcycleAdvanced} className="px-0">
                    <div>
                        <Row>
                            <Col md={6}>
                                <Field name="motorcycleSnapshot.shopMotorcycle.motorcycleYear">
                                    {({ field, meta }) => (
                                        <Form.Group className="mb-3">
                                            <Form.Label>Registration Year</Form.Label>
                                            <Form.Control {...field} type="text" readOnly isInvalid={meta.touched && !!meta.error} />
                                            <Form.Control.Feedback type="invalid">{meta.error}</Form.Control.Feedback>
                                        </Form.Group>
                                    )}
                                </Field>
                            </Col>

                            <Col md={6}>
                                <Field name="motorcycleSnapshot.shopMotorcycle.motorcycleModel.modelYear">
                                    {({ field, meta }) => (
                                        <Form.Group className="mb-3">
                                            <Form.Label>Model Year</Form.Label>
                                            <Form.Control {...field} type="text" readOnly isInvalid={meta.touched && !!meta.error} />
                                            <Form.Control.Feedback type="invalid">{meta.error}</Form.Control.Feedback>
                                        </Form.Group>
                                    )}
                                </Field>
                            </Col>

                            <Col md={6}>
                                <Field name="rentalPolicySnapshot.minAge">
                                    {({ field, meta }) => (
                                        <Form.Group className="mb-3">
                                            <Form.Label>Minimum Rider Age</Form.Label>
                                            <Form.Control {...field} type="text" isInvalid={meta.touched && !!meta.error} />
                                            <Form.Control.Feedback type="invalid">{meta.error}</Form.Control.Feedback>
                                        </Form.Group>
                                    )}
                                </Field>
                            </Col>

                            <Col md={6}>
                                <Field name="rentalPolicySnapshot.minExp">
                                    {({ field, meta }) => (
                                        <Form.Group className="mb-3">
                                            <Form.Label>Required Riding Experience (Years)</Form.Label>
                                            <Form.Control {...field} type="text" isInvalid={meta.touched && !!meta.error} />
                                            <Form.Control.Feedback type="invalid">{meta.error}</Form.Control.Feedback>
                                        </Form.Group>
                                    )}
                                </Field>
                            </Col>

                            <Col md={6}>
                                <Field name="rentalPolicySnapshot.licenseType">
                                    {({ field, meta }) => (
                                        <Form.Group className="mb-3">
                                            <Form.Label>License Class Requirement</Form.Label>
                                            <Form.Select {...field} isInvalid={meta.touched && !!meta.error}>
                                                <option value="AM">AM</option>
                                                <option value="B">B</option>
                                                <option value="A1">A1</option>
                                                <option value="A2">A2</option>
                                                <option value="A">A</option>
                                            </Form.Select>
                                        </Form.Group>
                                    )}
                                </Field>
                            </Col>
                        </Row>

                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Mileage Limit</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={!values?.rentalPolicySnapshot?.unlimitedMileage ? "LIMITED" : "UNLIMITED"}
                                        readOnly
                                    />
                                </Form.Group>
                            </Col>

                            {!values?.rentalPolicySnapshot?.unlimitedMileage && (
                                <>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>
                                                Mileage Allowance ({values?.shopSnapshot?.pickup?.shop?.distanceUnit})
                                            </Form.Label>
                                            <Form.Control type="text" value={values?.rentalPolicySnapshot?.mileageLimit || 0} readOnly />
                                        </Form.Group>
                                    </Col>

                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Paid Overage Available</Form.Label>
                                            <Form.Control
                                                type="text"
                                                value={(values?.rentalPolicySnapshot?.excessMileageFee || 0) > 0 ? "YES" : "NO"}
                                                readOnly
                                            />
                                        </Form.Group>
                                    </Col>

                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>
                                                Overage Price (per {values?.shopSnapshot?.pickup?.shop?.distanceUnit})
                                            </Form.Label>
                                            <InputGroup>
                                                <Form.Control type="text" value={values?.rentalPolicySnapshot?.excessMileageFee || 0} readOnly />
                                                <InputGroup.Text>
                                                    {values?.pricingSnapshot?.calculation?.customerCurrencyCode || "EUR"}
                                                </InputGroup.Text>
                                            </InputGroup>
                                        </Form.Group>
                                    </Col>
                                </>
                            )}
                        </Row>

                        <Row>
                            <Col md={6}>
                                <Field name="rentalPolicySnapshot.countryRestriction">
                                    {({ field, form }) => (
                                        <Form.Group className="mb-3">
                                            <Form.Check
                                                type="switch"
                                                label="International Travel Allowed"
                                                checked={field.value || false}
                                                onChange={(e) => form.setFieldValue(field.name, e.target.checked)}
                                            />
                                        </Form.Group>
                                    )}
                                </Field>
                            </Col>

                            <Col md={6}>
                                <Field name="rentalPolicySnapshot.canLeaveCity">
                                    {({ field, form }) => (
                                        <Form.Group className="mb-3 d-flex align-items-center">
                                            <Form.Check
                                                type="switch"
                                                label="Inter-City Travel Allowed"
                                                checked={field.value || false}
                                                onChange={(e) => form.setFieldValue(field.name, e.target.checked)}
                                            />
                                        </Form.Group>
                                    )}
                                </Field>
                            </Col>

                            <Col md={6}>
                                <Field name="rentalPolicySnapshot.canLeaveState">
                                    {({ field, form }) => (
                                        <Form.Group className="mb-3 d-flex align-items-center">
                                            <Form.Check
                                                type="switch"
                                                label="Inter-State Travel Allowed"
                                                checked={field.value || false}
                                                onChange={(e) => form.setFieldValue(field.name, e.target.checked)}
                                            />
                                        </Form.Group>
                                    )}
                                </Field>
                            </Col>
                        </Row>

                        <Row>
                            <Col md={6}>
                                <Field name="rentalPolicySnapshot.damageExcess">
                                    {({ field, meta }) => (
                                        <Form.Group className="mb-3">
                                            <Form.Label>Theft Excess</Form.Label>
                                            <InputGroup>
                                                <Form.Control {...field} type="text" isInvalid={meta.touched && !!meta.error} />
                                                <InputGroup.Text>
                                                    {values?.pricingSnapshot?.calculation?.customerCurrencyCode || "EUR"}
                                                </InputGroup.Text>
                                                <Form.Control.Feedback type="invalid">{meta.error}</Form.Control.Feedback>
                                            </InputGroup>
                                        </Form.Group>
                                    )}
                                </Field>
                            </Col>

                            <Col md={6}>
                                <Field name="rentalPolicySnapshot.theftExcess">
                                    {({ field, meta }) => (
                                        <Form.Group className="mb-3">
                                            <Form.Label>Damage Excess</Form.Label>
                                            <InputGroup>
                                                <Form.Control {...field} type="text" isInvalid={meta.touched && !!meta.error} />
                                                <InputGroup.Text>
                                                    {values?.pricingSnapshot?.calculation?.customerCurrencyCode || "EUR"}
                                                </InputGroup.Text>
                                                <Form.Control.Feedback type="invalid">{meta.error}</Form.Control.Feedback>
                                            </InputGroup>
                                        </Form.Group>
                                    )}
                                </Field>
                            </Col>

                            <Col md={6}>
                                <Field name="rentalPolicySnapshot.deposit">
                                    {({ field, meta }) => (
                                        <Form.Group className="mb-3">
                                            <Form.Label>Security Deposit</Form.Label>
                                            <InputGroup>
                                                <Form.Control {...field} type="text" isInvalid={meta.touched && !!meta.error} />
                                                <InputGroup.Text>
                                                    {values?.pricingSnapshot?.calculation?.customerCurrencyCode || "EUR"}
                                                </InputGroup.Text>
                                                <Form.Control.Feedback type="invalid">{meta.error}</Form.Control.Feedback>
                                            </InputGroup>
                                        </Form.Group>
                                    )}
                                </Field>
                            </Col>
                        </Row>
                    </div>
                </Collapse>
            </FormSection>

            {/* ---------------- Accessories ---------------- */}
            <FormSection title="Shop & Motorcycle Accessories" level={2}>
                <Row>
                    <Col md={6}>
                        <Row>
                            <Col md={12}>
                                <AccessoryComparisonTable
                                    mode="display"
                                    title="Original Extras"
                                    accessories={originalRentalOrderExtra || []}
                                    customerCurrencyCode={
                                        values?.pricingSnapshot?.calculation?.customerCurrencyCode || "EUR"
                                    }
                                    totalCents={originalServicesTotalCentsRef?.current || 0}
                                />
                            </Col>
                        </Row>
                    </Col>

                    <Col md={6}>
                        <Row>

                            <Col md={12}>
                                <AccessoryComparisonTable
                                    title="Current extras"
                                    customerCurrencyCode={values?.pricingSnapshot?.calculation?.customerCurrencyCode || "EUR"}
                                    currentAccessories={values?.rentalOrderExtra || []}
                                    setShowAccessoriesModal={setShowAccessoriesModal}
                                />
                            </Col>
                        </Row>
                    </Col>


                    <AccessoriesDrawer
                        show={showAccessoriesModal}
                        onClose={() => setShowAccessoriesModal(false)}
                        onSave={updateAccessories}
                        values={values}
                        availableExtras={shopMotorcycleExtras || []}
                        currentAccessories={values?.rentalOrderExtra || []}
                        customerCurrencyCode={values?.pricingSnapshot?.calculation?.customerCurrencyCode || "EUR"}
                        sizeOptions={sizeOptions}
                        toCents={toCents}
                        fromCents={fromCents}
                    />

                </Row>
            </FormSection>
        </FormSection>
    );
};

export default AssetsTab;